import DepositComponent from "@app/common/components/depositComponent/DepositComponent";
import { IDepositState } from "@app/common/components/depositComponent/depositComponentInter";
import { warningNotification } from "@app/common/components/notifications";
import { formattedDataSelect } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import { PatientList, ExternalClientList } from "@app/services/common/types";
import {
  useDeliveryDispense,
  useGetPatientPrice,
  useGetWalletBalance
} from "@app/services/drugDelivery/hooks";
import {
  DeliveryPayload,
  MedicinePay,
  OrderListResponse,
  PricePatientPayload
} from "@app/services/drugDelivery/types";
import { useAuthentication } from "@app/store/useAuthtentication";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { useGenericData } from "@app/store/useGenericData";
import { Box, Button, Drawer, NumberFormatter, Select, Table, Text, Tooltip } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";

const DeliveryFormToSend = ({
  isOpen,
  onClose,
  data,
  patient
}: {
  isOpen: boolean;
  data?: OrderListResponse;
  patient?: PatientList | ExternalClientList | null;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can(RBAC_ACTIONS.CREATE, MODULE_PERMISSION_PREFIX.MEDICINE_DELIVERY);
  const { dataBiowel } = useAuthentication((store) => store.auth);
  const { deliveryBy, fromPendingOrder } = useDeliveryData((store) => store.dataDispense);
  const { siteId, siteResolutions } = useGenericData((store) => store.genericData);

  const [collection, setCollection] = useState<IDepositState[]>([]);

  const resolutions = formattedDataSelect(siteResolutions, "name", "id");
  const isFreeSale = ["freeSale", "freeSaleWithOrder"].includes(deliveryBy ?? "");
  const withInsurance = ["freeSaleWithOrder", "insurance"].includes(deliveryBy ?? "");

  const [resolutionId, setResolutionId] = useState("");
  const [prices, setPrices] = useState({
    companyPrice: 0,
    patientPrice: 0
  });

  const shouldShowCollectionSection = prices.patientPrice !== 0 && !fromPendingOrder;
  const isExternalPatientDelivery = deliveryBy === "freeSale";
  const resolvedPatientId = Number(patient?.id ?? data?.orderInfo?.patient?.id ?? 0);

  const { data: walletBalance } = useGetWalletBalance(
    {
      patientId: resolvedPatientId,
      isExternalPatient: isExternalPatientDelivery ? 1 : undefined
    },
    isOpen && resolvedPatientId > 0
  );

  const walletAmount = Number(walletBalance?.results?.totalPendingAmount ?? 0);

  const closeDrawer = () => {
    onClose();
    setResolutionId("");
  };

  const createPriceRequest = useGetPatientPrice(async (res) => {
    const doAfter = () => {
      setPrices(res.results as { companyPrice: number; patientPrice: number });
    };

    await doAfter();
  });

  const onSubmitPricePatient = async (value: PricePatientPayload) => {
    await createPriceRequest.mutateAsync(value);
  };

  useEffect(() => {
    if (isOpen && (data?.orderInfo?.orderId || isFreeSale) && data?.medicines) {
      onSubmitPricePatient({
        orderId: withInsurance ? Number(data?.orderInfo?.orderId) : undefined,
        externalClientId: withInsurance ? undefined : (patient?.id ?? ""),
        patientId: !withInsurance ? undefined : (patient?.id ?? data?.orderInfo?.patient?.id ?? ""),
        isFreeSale: isFreeSale,
        medicines: data?.medicines.map((medicine) => ({
          medId: medicine.id ?? "",
          qty: medicine.qtyDispensed ?? "",
          articleId: medicine.articleId
        }))
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.medicines, data?.orderInfo, isOpen]);

  const createDeliveryRequest = useDeliveryDispense(async (res) => {
    const results = res.results as {
      dispensary_id?: number;
      electronic_invoice?: { pdf_url?: string };
    };
    const disId = results?.dispensary_id ?? res.results;
    const pdfUrl = results?.electronic_invoice?.pdf_url;

    if (pdfUrl) {
      navigate(routes.getDeliveryDispensationDetail, {
        state: { disId, pdfUrl, orderInfo: { patient, ...data?.orderInfo } }
      });
    } else if (isFreeSale && !withInsurance) {
      navigate(routes.drugDelivery, {
        state: { disId, orderInfo: { patient, ...data?.orderInfo } }
      });
    } else {
      navigate(routes.getDeliveryDispensationDetail, {
        state: { disId, orderInfo: { patient, ...data?.orderInfo } }
      });
    }
  });

  const onChangeDeposit = (deposits: IDepositState[]) => {
    setCollection(deposits);
  };

  const onSubmit = async (value: DeliveryPayload) => {
    if (!canCreate) {
      return;
    }
    await createDeliveryRequest.mutateAsync({ ...value });
  };

  const onSubmitValidations = () => {
    const isParticular = data?.orderInfo?.medicalOrderType === "particular";
    const collectionAmount = collection.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const walletCollectionAmount = collection
      .filter((deposit) => deposit.type === "wallet")
      .reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

    const dispensedMedicines =
      data?.medicines?.filter((medicine) => Number(medicine.qtyDispensed) > 0) ?? [];
    const isTopInventoryDispense = dispensedMedicines.some((medicine) => medicine.isTop);
    const hasMissingTopWarehouseData = dispensedMedicines.some(
      (medicine) => medicine.isTop && (!medicine.warehouseCode || !medicine.warehouseName)
    );

    if (shouldShowCollectionSection && collectionAmount < prices.patientPrice) {
      warningNotification("El valor de los depósitos es menor al valor a pagar.");
      return;
    }

    if (walletCollectionAmount > walletAmount) {
      warningNotification("El valor ingresado en bolsillo supera el saldo disponible.");
      return;
    }

    if (isTopInventoryDispense && hasMissingTopWarehouseData) {
      warningNotification(
        "Los medicamentos de inventario TOP deben incluir código y nombre de bodega."
      );
      return;
    }

    onSubmit({
      isPendingOrder: fromPendingOrder,
      free_sale: isFreeSale || isParticular ? true : undefined,
      isTop: isTopInventoryDispense ? true : undefined,
      siteId: Number(siteId),
      additionalSupports: data?.additionalSupports
        ?.filter((x) => x.file !== null)
        ?.map((supports) => ({
          file: supports.file ?? "",
          rcsId: supports.rcsId
        })),
      disFacePicture: data?.patientFacePicture || undefined,
      disRequestByName: data?.receiveName || undefined,
      disRequestByDocumentId: data?.receiveDocument || undefined,
      disSignature: data?.patientSignature || undefined,
      morId: withInsurance ? Number(data?.orderInfo?.orderId) : undefined,
      patientId: !withInsurance ? undefined : (patient?.id ?? data?.orderInfo?.patient?.id ?? ""),
      externalClientId: withInsurance ? undefined : (patient?.id ?? ""),
      patientDocumentPictures: data?.patientDocumentPictures || undefined,
      disRequestByDocumentType: Number(data?.disRequestByDocumentType) || undefined,
      medicines: dispensedMedicines.map((medicine) => ({
        companyPrice: prices.companyPrice ?? "",
        id: medicine.id ?? "",
        articleId: medicine.articleId ?? medicine.id ?? "",
        batchId: medicine.batchId,
        patientPrice: prices.patientPrice ?? "",
        lot: medicine.lot ?? "",
        warehouseCode: medicine.isTop ? (medicine.warehouseCode ?? "") : undefined,
        warehouseName: medicine.isTop ? (medicine.warehouseName ?? "") : undefined,
        presentationId: medicine.presentationId ?? "",
        qtyDelivered: medicine.qtyDispensed ?? "",
        qtyDispensed: medicine.qtyDelivered ?? "",
        qtyOrder: medicine.qtyOrder ?? ""
      })),
      payment: !shouldShowCollectionSection
        ? undefined
        : {
            userId: dataBiowel.idUser,
            deposits: collection,
            resolutionId,
            siteId: String(siteId)
          },
      authorizationNumber: data?.authorizationNumber || undefined,
      authorizationDate: data?.authorizationDate || undefined,
      observations: data?.observations || undefined
    });
  };

  const renderRow = (option: MedicinePay, idx: number) => {
    return (
      <Table.Tr key={option?.id} data-testid={`deliveryformtosend-medicine-row-${idx}`}>
        <Table.Td>{option?.name}</Table.Td>
        <Table.Td>{option?.lot}</Table.Td>
        <Table.Td>{option?.description}</Table.Td>
        <Table.Td ta={"center"}>{option?.qtyDispensed ?? "-"} unds.</Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = (medicines: MedicinePay[]) => {
    return (
      <Table
        className="biowel-table"
        my={"sm"}
        highlightOnHover={false}
        verticalSpacing={"0.3rem"}
        data-testid="deliveryformtosend-medicines-table"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th bg={"#fff"} ta={"start"} className="secondary-color">
              Referencia
            </Table.Th>
            <Table.Th bg={"#fff"} ta={"start"} className="secondary-color">
              Lote
            </Table.Th>
            <Table.Th bg={"#fff"} ta={"start"} className="secondary-color">
              Descripción
            </Table.Th>
            <Table.Th bg={"#fff"} ta={"center"} className="secondary-color">
              Cantidad
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {medicines ? (
            <>
              {medicines
                .filter((medicine) => Number(medicine.qtyDispensed) > 0)
                .map((medicine, idx) => renderRow(medicine, idx))}
              <Table.Tr data-testid="deliveryformtosend-copayment-row">
                <Table.Td>
                  {" "}
                  <Text fz="md" pt={"sm"} flex={1} c="brand-color" fw={"bold"}>
                    Total a pagar
                  </Text>
                </Table.Td>
                <Table.Td></Table.Td>
                <Table.Td ta={"center"}>
                  <Text fz="md" pt={"sm"} flex={1} c="brand-color" fw={"bold"}>
                    <NumberFormatter prefix="$ " value={prices?.patientPrice} thousandSeparator />
                  </Text>
                </Table.Td>
              </Table.Tr>
            </>
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12}>
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Drawer opened={isOpen} onClose={closeDrawer} title={"Resumen de compra"} size={"lg"}>
      <Box className="flex flex-column justify-between" h={"100%"}>
        <Box>
          {shouldShowCollectionSection && (
            <>
              <Text fz="md" pt={"sm"} ml="xs" flex={1} c="brand-color" fw={"bold"}>
                Selecciona el método de pago
              </Text>
              <Select
                className="text-secondary"
                ml={"xs"}
                mb={"sm"}
                label="Resolución de facturación"
                required
                placeholder={"Seleccionar..."}
                data={resolutions}
                value={resolutions.find((res) => res.value === resolutionId)?.value}
                onChange={(option) => {
                  setResolutionId(option ?? "");
                }}
                data-testid="dispensation-medicine-delivery-payment-resolution-select"
              />

              <DepositComponent
                payment={prices?.patientPrice}
                onChange={onChangeDeposit}
                walletAmount={walletAmount}
              />
            </>
          )}
          <Text fz="md" pt={"sm"} ml="xs" flex={1} c="brand-color" fw={"bold"}>
            Resumen de entrega
          </Text>
          {renderTable(
            data?.medicines?.map((medicine) => ({
              companyPrice: prices.companyPrice ?? "",
              id: medicine.id ?? "",
              name: medicine.name ?? "",
              description: medicine.description ?? "",
              lot: medicine.lot ?? "",
              patientPrice: prices.patientPrice ?? "",
              presentationId: medicine.presentationId ?? "",
              qtyDelivered: medicine.qtyDelivered ?? "",
              qtyDispensed: medicine.qtyDispensed ?? "",
              qtyOrder: medicine.qtyOrder ?? ""
            })) ?? []
          )}
        </Box>

        <Box pb={"md"} className="flex justify-end gap-3" data-testid="deliveryformtosend-buttons">
          <Button variant="outline" onClick={closeDrawer} data-testid="deliveryformtosend-cancel">
            Cancelar
          </Button>
          <Tooltip
            label={canCreate ? "Generar resumen de dispensación" : RBAC_LABELS.NO_PERMISSION}
            withArrow
          >
            <Button
              variant="filled"
              disabled={!canCreate}
              className={!canCreate ? "noPermissionDisabled" : ""}
              onClick={() => {
                if (!canCreate) {
                  return;
                }
                onSubmitValidations();
              }}
              data-testid="deliveryformtosend-submit"
            >
              Generar resumen de dispensación
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DeliveryFormToSend;
